import GoogleAnalytics from '@/components/GoogleAnalytics'
import Layout from '@/components/Layout'
import ThemeStyles from '@/components/ThemeStyles'
import { preloadTemplates } from '@/wp-templates'
import client from '@/utilities/apolloClient'
import { ApolloProvider } from '@apollo/client'
import { FaustProvider } from '@faustwp/core'
import type { AppProps } from 'next/app'
import { Instrument_Sans, Poppins } from 'next/font/google'
import { useRouter } from 'next/router'
import 'normalize.css/normalize.css'
import { useEffect } from 'react'
import '../faust.config'
import '../styles/main.scss'

const poppins = Poppins({
  subsets: ['latin'],
  weight: ['300', '400', '500', '600', '700'],
  style: 'normal',
  display: 'swap',
  variable: '--font-poppins'
})

const instrumentSans = Instrument_Sans({
  subsets: ['latin'],
  weight: 'variable',
  style: 'normal',
  display: 'swap',
  variable: '--font-instrument-sans'
})

// Italics only show up in post content, so load them on demand rather than preloading them on every page
const poppinsItalic = Poppins({
  subsets: ['latin'],
  weight: ['300', '400', '500', '600', '700'],
  style: 'italic',
  display: 'swap',
  preload: false,
  variable: '--font-poppins-italic'
})

const instrumentSansItalic = Instrument_Sans({
  subsets: ['latin'],
  weight: 'variable',
  style: 'italic',
  display: 'swap',
  preload: false,
  variable: '--font-instrument-sans-italic'
})

// Run once the page has finished loading and the main thread is free, so it never competes with the
// current page's own requests
function whenIdleAfterLoad(callback: () => void) {
  const schedule = () => ('requestIdleCallback' in window ? window.requestIdleCallback(callback) : setTimeout(callback, 200))
  if (document.readyState === 'complete') schedule()
  else window.addEventListener('load', schedule, { once: true })
}

export default function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter()
  useEffect(() => {
    import('aos').then((AOS) => {
      AOS.default.init({
        duration: 1200,
        once: true
      })
    })

    // Template views are code-split; fetch the rest in the background so a client-side navigation to a
    // different template renders immediately instead of waiting on its chunk
    whenIdleAfterLoad(preloadTemplates)
  }, [])

  return (
    <div className={`${poppins.variable} ${instrumentSans.variable} ${poppinsItalic.variable} ${instrumentSansItalic.variable}`}>
      <GoogleAnalytics />
      <ThemeStyles />
      <FaustProvider pageProps={pageProps}>
        <ApolloProvider client={client}>
          <Layout siteData={pageProps.__siteData}>
            <Component {...pageProps} key={router.asPath} />
          </Layout>
        </ApolloProvider>
      </FaustProvider>
    </div>
  )
}
